import { Button, Flex, Typography } from 'antd';
import { FC, useState } from 'react';

import { useAdminAccountingUsageController_getPlan } from '@api-client/generated/AdminAccountingUsageController/getPlan';
import CompanyDetailsInfoCell from '@entities/company-details/CompanyDetailsInfoCell';
import { useAccount } from '@hooks';

import AccountingPlanModal from './AccountingPlanModal';
import { PLAN_TYPE_LABEL, formatPlanAmount } from './format';

const { Text } = Typography;

type Props = { companyId: string; companyName: string };

/**
 * The company's accounting plan in its Service section, with the same "Edit plan" popup as the
 * Accounting usage page. Admin only — the plan API is @AdminRoles(Admin) — so kyc and accountant
 * users, who see the rest of the section, get nothing here rather than a failing request.
 */
const AccountingPlanInfo: FC<Props> = ({ companyId, companyName }) => {
  const { role } = useAccount();
  const isAdmin = role === 'admin';
  const [open, setOpen] = useState(false);
  const {
    data: plan,
    isLoading,
    error,
  } = useAdminAccountingUsageController_getPlan({
    params: { companyId },
    config: { enabled: isAdmin },
  });

  if (!isAdmin) return null;

  const value = isLoading ? (
    '…'
  ) : error ? (
    <Text type="danger">Could not load the plan</Text>
  ) : (
    <Flex align="center" gap={8} wrap="wrap">
      {plan?.planType && plan.planLimit !== null ? (
        <Text>
          {PLAN_TYPE_LABEL[plan.planType]} ·{' '}
          {formatPlanAmount(plan.planLimit, plan.planType)} per year
        </Text>
      ) : (
        <Text type="secondary">No plan</Text>
      )}
      <Button size="small" onClick={() => setOpen(true)}>
        Edit plan
      </Button>
    </Flex>
  );

  return (
    <>
      <CompanyDetailsInfoCell label="Accounting plan" value={value} isVisible />
      {open && (
        <AccountingPlanModal
          companyId={companyId}
          companyName={companyName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default AccountingPlanInfo;
